import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useCapture } from "@/context/CaptureContext";
import { CaptureThumb, fmtDate } from "@/components/common";
import { Camera, Mic, FileText, File, LinkIcon, Clock, Inbox } from "@/lib/icons";

const QUICK = [
  { key: "note", label: "Note", icon: FileText, color: "#10B981" },
  { key: "photo", label: "Photo", icon: Camera, color: "#3B82F6" },
  { key: "audio", label: "Voice", icon: Mic, color: "#A855F7" },
  { key: "file", label: "File", icon: File, color: "#F59E0B" },
  { key: "link", label: "Link", icon: LinkIcon, color: "#F97316" },
];

const STAT_META = [
  { key: "captures", label: "Captures", color: "#2563EB", icon: Camera },
  { key: "to_catalog", label: "To Curate", color: "#F59E0B", icon: FileText },
  { key: "projects", label: "Projects", color: "#A855F7", icon: File },
  { key: "people", label: "People", color: "#EF4444", icon: FileText },
  { key: "places", label: "Places", color: "#14B8A6", icon: FileText },
];

export default function Home() {
  const { openCapture } = useCapture();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  const load = () => api.get("/home").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => {
    load();
    window.addEventListener("dl-saved", load);
    return () => window.removeEventListener("dl-saved", load);
  }, []);

  if (!data) return <div className="text-slate-500">Loading…</div>;
  const greeting = new Date().getHours() < 12 ? "Good Morning" : new Date().getHours() < 18 ? "Good Afternoon" : "Good Evening";

  return (
    <div className="max-w-6xl mx-auto space-y-8 dl-rise">
      {/* Greeting */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="font-mono-label text-[11px] text-sky-400 mb-1">{fmtDate(new Date().toISOString()).split("·")[0]}</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-white tracking-tight">{greeting}, {data.greeting_name}.</h1>
          <p className="text-slate-400 mt-1">Your world is worth capturing.</p>
        </div>
      </div>

      {/* Quick capture */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {QUICK.map((q) => {
          const Icon = q.icon;
          return (
            <button key={q.key} data-testid={`quick-capture-${q.key}`} onClick={() => openCapture(q.key)}
              className="dl-card p-4 flex flex-col items-center gap-2 hover:-translate-y-0.5">
              <span className="h-11 w-11 rounded-xl grid place-items-center" style={{ background: q.color + "22", color: q.color }}><Icon size={20} /></span>
              <span className="text-sm font-medium text-white">{q.label}</span>
            </button>
          );
        })}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {STAT_META.map((s) => (
          <div key={s.key} data-testid={`stat-${s.key}`} className="dl-card p-4">
            <div className="text-3xl font-display font-bold text-white">{data.stats[s.key]}</div>
            <div className="text-xs mt-1" style={{ color: s.color }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Recent captures */}
      <section>
        <SectionHead title="Recent Captures" to="/catalog" />
        {data.recent_captures.length === 0 ? (
          <p className="text-sm text-slate-500">No captures yet. Use a quick action above to begin.</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {data.recent_captures.map((c) => <CaptureThumb key={c.id} cap={c} />)}
          </div>
        )}
      </section>

      {/* Two columns: Inbox + Collections */}
      <div className="grid lg:grid-cols-2 gap-6">
        <section>
          <SectionHead title="Loading Dock" to="/inbox" />
          <div className="dl-card divide-y" style={{ borderColor: "var(--dl-border)" }}>
            {data.inbox.length === 0 ? (
              <div className="p-6 text-sm text-slate-500 flex items-center gap-2"><Inbox size={16} /> Nothing awaiting review. All clear.</div>
            ) : data.inbox.map((c) => (
              <Link key={c.id} to={`/captures/${c.id}`} className="flex items-center gap-3 p-3 hover:bg-white/5" data-testid={`inbox-item-${c.id}`}>
                <span className="h-2 w-2 rounded-full shrink-0" style={{ background: "#F59E0B" }} />
                <span className="flex-1 text-sm text-white truncate">{c.title || "Untitled"}</span>
                <span className="text-[11px] text-slate-500">{fmtDate(c.created_at)}</span>
              </Link>
            ))}
          </div>
        </section>

        <section>
          <SectionHead title="Collections" to="/connect" />
          <div className="grid grid-cols-2 gap-3">
            {data.collections.map((c) => (
              <Link key={c.id} to={`/collections/${c.id}`} className="dl-card p-4" data-testid={`home-collection-${c.id}`}>
                <span className="h-9 w-9 rounded-lg grid place-items-center mb-3" style={{ background: c.color + "22", color: c.color }}><File size={18} /></span>
                <div className="text-sm font-semibold text-white truncate">{c.name}</div>
                <div className="text-[11px] text-slate-500 mt-0.5">{c.item_count} items · {c.kind}</div>
              </Link>
            ))}
          </div>
        </section>
      </div>

      {/* Follow-ups */}
      {data.tasks.length > 0 && (
        <section>
          <SectionHead title="Resume Work & Follow-ups" to="/continue" />
          <div className="dl-card divide-y" style={{ borderColor: "var(--dl-border)" }}>
            {data.tasks.map((t) => (
              <div key={t.id} className="flex items-center gap-3 p-3">
                <Clock size={15} className="text-teal-400 shrink-0" />
                <span className="flex-1 text-sm text-white">{t.title}</span>
                {t.due_date && <span className="text-[11px] text-slate-500">due {new Date(t.due_date).toLocaleDateString()}</span>}
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function SectionHead({ title, to }) {
  return (
    <div className="flex items-center justify-between mb-3">
      <h2 className="font-display text-lg font-semibold text-white">{title}</h2>
      <Link to={to} className="text-xs text-sky-400 hover:underline">View all</Link>
    </div>
  );
}
